import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';
import { toHistoryKey, getLatestHistoryEntry } from './useSensorDataTab.utils';
import S from './SensorDataTab.module.scss';

const PALETTE = [
  '#a6163f', '#2980b9', '#27ae60', '#f39c12',
  '#8e44ad', '#16a085', '#c0392b', '#1a5276',
];

const toISO = (timeUnix) => new Date(timeUnix * 1000).toISOString();

const SensorChartTab = ({ rows, historyMap, getSensorNameById }) => {
  const traces = useMemo(() => {
    if (!rows.length) return [];

    const groups = {};
    rows.forEach((row) => {
      const key = `${row._sensorId}_${row.position_in_sensor}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });

    let colorIdx = 0;
    const allTraces = [];

    Object.entries(groups).forEach(([key, groupRows]) => {
      const sorted = [...groupRows].sort((a, b) => a.time_unix - b.time_unix);
      const [sId, pos] = key.split('_');
      const sensorName = getSensorNameById(Number(sId));
      const label = `${sensorName} P${pos}`;
      const color = PALETTE[colorIdx % PALETTE.length];
      colorIdx++;

      const deletedRows = sorted.filter((r) => {
        const histKey = toHistoryKey(r._sensorId, r.pk_test);
        const entries = historyMap[histKey] ?? [];
        return getLatestHistoryEntry(entries)?.update_type === 'Delete';
      });
      const deletedIdSet = new Set(deletedRows.map((r) => r.id));

      const activeRows = sorted.filter((r) => !deletedIdSet.has(r.id));
      const dirtyRows = activeRows.filter((r) => r._dirty);

      // Main line trace — all active rows (line stays continuous over modified points)
      if (activeRows.length) {
        allTraces.push({
          x: activeRows.map((r) => toISO(r.time_unix)),
          y: activeRows.map((r) => r.value),
          type: 'scatter',
          mode: 'lines+markers',
          name: label,
          line: { color, width: 2 },
          marker: { size: 7, color },
          hovertemplate: `<b>${label}</b><br>Date : %{x}<br>Valeur : %{y}<extra></extra>`,
        });
      }

      // Red ring around modified points (circle-open overlay)
      if (dirtyRows.length) {
        allTraces.push({
          x: dirtyRows.map((r) => toISO(r.time_unix)),
          y: dirtyRows.map((r) => r.value),
          customdata: dirtyRows.map((r) => r.old_value ?? '—'),
          type: 'scatter',
          mode: 'markers',
          name: `${label} — modifié`,
          marker: {
            size: 18,
            color: 'rgba(0,0,0,0)',
            symbol: 'circle-open',
            line: { color: '#e74c3c', width: 2.5 },
          },
          hovertemplate:
            `<b>${label} — Modifié</b><br>` +
            `Date : %{x}<br>` +
            `Nouvelle valeur : %{y}<br>` +
            `Ancienne valeur : %{customdata}<extra></extra>`,
        });
      }

      // Gray open circle at the original position before edit
      const dirtyWithOld = dirtyRows.filter((r) => r.old_value != null);
      if (dirtyWithOld.length) {
        allTraces.push({
          x: dirtyWithOld.map((r) => toISO(r.old_time_unix ?? r.time_unix)),
          y: dirtyWithOld.map((r) => r.old_value),
          type: 'scatter',
          mode: 'markers',
          name: `${label} — avant modif.`,
          marker: {
            size: 9,
            color: 'rgba(0,0,0,0)',
            symbol: 'circle-open',
            line: { color: '#aaaaaa', width: 2 },
          },
          hovertemplate: `<b>${label} — Avant modif.</b><br>Date : %{x}<br>Ancienne valeur : %{y}<extra></extra>`,
        });
      }

      // Deleted rows — red X
      if (deletedRows.length) {
        allTraces.push({
          x: deletedRows.map((r) => toISO(r.time_unix)),
          y: deletedRows.map((r) => r.value),
          type: 'scatter',
          mode: 'markers',
          name: `${label} — supprimé`,
          marker: {
            size: 13,
            color: '#e74c3c',
            symbol: 'x',
            line: { color: '#e74c3c', width: 2.5 },
          },
          hovertemplate: `<b>${label} — Supprimé</b><br>Date : %{x}<br>Valeur : %{y}<extra></extra>`,
        });
      }
    });

    return allTraces;
  }, [rows, historyMap, getSensorNameById]);

  return (
    <div className={S.chartInlineWrapper}>
      <div className={S.chartInlineHeader}>
        <span className={S.chartInlineTitle}>Visualisation des modifications</span>
      </div>

      <div className={S.chartPlotArea}>
      <Plot
        data={traces}
        layout={{
          autosize: true,
          margin: { t: 10, r: 20, b: 70, l: 55 },
          paper_bgcolor: '#ffffff',
          plot_bgcolor: '#fafafa',
          xaxis: {
            title: 'Date',
            type: 'date',
            tickformat: '%d/%m %H:%M',
            gridcolor: '#e0e0e0',
            linecolor: '#cccccc',
            zeroline: false,
          },
          yaxis: {
            title: 'Valeur',
            gridcolor: '#e0e0e0',
            linecolor: '#cccccc',
            zeroline: false,
          },
          legend: {
            orientation: 'h',
            y: -0.28,
            x: 0,
            font: { size: 10 },
          },
          hovermode: 'closest',
        }}
        config={{
          responsive: true,
          displayModeBar: true,
          scrollZoom: true,
          displaylogo: false,
          modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        }}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler
      />
      </div>

      <div className={S.chartLegend}>
        <span className={S.chartLegendItem}>
          <span className={S.legendLine} />
          Valeur actuelle
        </span>
        <span className={S.chartLegendItem}>
          <span className={S.legendRing} />
          Modifié
        </span>
        <span className={S.chartLegendItem}>
          <span className={S.legendCircleOpen}>○</span>
          Avant modif.
        </span>
        <span className={S.chartLegendItem}>
          <span className={S.legendX}>✕</span>
          Supprimé
        </span>
      </div>
    </div>
  );
};

export default SensorChartTab;
